import { STATUS_TONES, type StatusLevel } from '@hgc/ui';
import { useId, type JSX } from 'react';

import type { VehicleState } from '@hgc/core';

import { useLinkStatus, useNow, useVehicleState } from '../runtime/hooks';
import {
  NO_DATA,
  attitudeOf,
  buildHudReadouts,
  cardinalLabel,
  headingTicks,
  pitchMarks,
  type AttitudeModel,
  type HudReadout,
} from './model';

/**
 * HUD：姿态指示（横滚/俯仰刻度）+ 航向带 + 顶部关键读数。
 *
 * 两条不可退让的规则：
 *
 * 1. **缺失不是 0。** 没有姿态数据时**不画**天地线，而是画一张写着"无姿态数据"的底面。
 *    把 `null` 当成 0 画出来的姿态仪会显示"完全水平" —— 一个看起来正常的假读数，
 *    比一块空白危险得多。
 * 2. **过期必须看得见。** 姿态数据的阈值是 1 秒（`TELEMETRY_STALE_MS`），超过之后
 *    整个 HUD 降饱和、加一条写明"多久没更新"的横幅。原因是链路静默时**没有新帧到达**，
 *    组件不会重渲染，界面会自己停在最后一帧的样子 —— 那正是最需要告警的时刻。
 *
 * 用 SVG 而不是 3D 库：这里要画的全是二维刻度，WebGL 只会带来一个几十 KB 的依赖
 * 和一套自己的坐标约定，而这个界面必须能在没有 GPU 的平板上跑。
 */

const TONE_TEXT: Readonly<Record<StatusLevel, string>> = {
  neutral: STATUS_TONES.neutral.text,
  ok: STATUS_TONES.ok.text,
  info: STATUS_TONES.info.text,
  caution: STATUS_TONES.caution.text,
  warning: STATUS_TONES.warning.text,
  critical: STATUS_TONES.critical.text,
};

/** `Rotate` 需要的是"度"，SVG 用屏幕坐标（y 向下），所以正角度 = 顺时针 */
const DEG = Math.PI / 180;

export interface HudProps {
  readonly state: VehicleState | null;
  readonly now: number;
  /** 没有数据的原因。`state` 为 `null` 时它是界面上唯一能解释空白的文字 */
  readonly emptyReason: string;
}

export function Hud({ state, now, emptyReason }: HudProps): JSX.Element {
  if (state === null) {
    return <HudShell hudState="missing" banner={emptyReason} level="warning" state={null} now={now} />;
  }

  const attitude = attitudeOf(state, now);
  const readouts = buildHudReadouts(state, now);
  const hudState = attitude.drawable ? (attitude.stale ? 'stale' : 'live') : 'missing';

  const banner = !attitude.drawable
    ? '没有姿态数据：还没有收到 ATTITUDE —— 这里不画天地线，也不把姿态当成 0，因为"看起来水平"和"确实水平"是两回事'
    : attitude.stale
      ? `姿态数据已过期：${attitude.ageLabel}更新（ATTITUDE 的阈值是 1 秒）—— 下面显示的不是实时姿态，不要据此判断机体姿态`
      : null;

  return (
    <HudShell
      hudState={hudState}
      banner={banner}
      level={attitude.drawable ? 'warning' : 'critical'}
      state={state}
      now={now}
      attitude={attitude}
      readouts={readouts}
    />
  );
}

function HudShell({
  hudState,
  banner,
  level,
  state,
  now,
  attitude,
  readouts,
}: {
  readonly hudState: 'live' | 'stale' | 'missing';
  readonly banner: string | null;
  readonly level: StatusLevel;
  readonly state: VehicleState | null;
  readonly now: number;
  readonly attitude?: AttitudeModel;
  readonly readouts?: readonly HudReadout[];
}): JSX.Element {
  const tone = STATUS_TONES[level];

  return (
    <section
      data-hgc-hud=""
      data-hgc-hud-state={hudState}
      aria-label="飞行 HUD"
      className="flex flex-col gap-2 rounded-card border border-line bg-surface-1 p-3"
    >
      {banner === null ? null : (
        <p
          data-hgc-hud-banner=""
          className={`m-0 rounded-control border-l-4 px-2 py-1.5 text-xs font-semibold ${tone.surface} ${tone.borderLeft} text-fg`}
        >
          {banner}
        </p>
      )}

      {/*
        整表降饱和：`filter` 会把这个元素变成 `position: fixed` 后代的包含块，
        所以**它下面不许挂浮层**（确认框由 `ConfirmProvider` 渲染在这一层之外）。
        用 `opacity` 之外的表达方式是因为"降饱和但保持可读"正是这里要的效果：
        变灰会让人以为界面坏了，降饱和加明确文字才是"数据不可信"。
      */}
      <div
        data-hgc-hud-content=""
        className="flex flex-col gap-3"
        style={hudState === 'stale' ? { filter: 'saturate(0.2)', opacity: 0.9 } : undefined}
      >
        {readouts === undefined ? (
          <div className="flex flex-col items-center gap-1 rounded-control bg-surface-2 px-3 py-6 text-center">
            <span className="text-sm font-medium text-fg">没有读数</span>
            <span className="text-xs text-fg-muted">链路建立后这里会显示地速、空速、高度、爬升率、模式与解锁状态。</span>
          </div>
        ) : (
          <div className="grid grid-cols-4 gap-2 xl:grid-cols-7">
            {readouts.map((readout) => (
              <ReadoutCell key={readout.id} readout={readout} />
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-start gap-3">
          <AttitudeIndicator attitude={attitude ?? null} />
          <div className="min-w-0 flex-1">
            <HeadingTape attitude={attitude ?? null} />
            <p className="m-0 mt-2 text-[10px] leading-relaxed text-fg-subtle">
              {state === null
                ? '没有车辆状态：链路未连接或还没有收到任何帧。'
                : `姿态与航向来自 ATTITUDE / GLOBAL_POSITION_INT，读数时刻 ${new Date(now).toLocaleTimeString('zh-CN', { hour12: false })}。`}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function ReadoutCell({ readout }: { readonly readout: HudReadout }): JSX.Element {
  return (
    <div
      data-hgc-readout={readout.id}
      data-hgc-readout-freshness={readout.stale ? 'stale' : 'fresh'}
      className="flex min-w-0 flex-col gap-0.5 rounded-control border border-line bg-surface-2 px-2 py-1.5"
    >
      <span className="truncate text-[10px] text-fg-subtle">{readout.label}</span>
      <span className="flex min-w-0 items-baseline gap-1">
        <span
          data-hgc-readout-value=""
          className={`font-mono text-lg leading-tight tabular-nums ${TONE_TEXT[readout.level]}`}
        >
          {readout.value}
        </span>
        {readout.unit === '' ? null : <span className="text-[10px] text-fg-muted">{readout.unit}</span>}
      </span>
      {readout.stale ? (
        <span className="truncate text-[10px] font-semibold text-warning">{readout.ageLabel}更新</span>
      ) : (
        <span className="truncate text-[10px] text-fg-subtle">{readout.source}</span>
      )}
    </div>
  );
}

const RADIUS = 100;
const PX_PER_DEG = 4;

function AttitudeIndicator({ attitude }: { readonly attitude: AttitudeModel | null }): JSX.Element {
  const clipId = `hgc-attitude-${useId().replaceAll(':', '')}`;
  const drawable = attitude?.drawable === true;
  const roll = attitude?.rollDeg ?? 0;
  const pitch = attitude?.pitchDeg ?? 0;
  const rollText = attitude?.rollDeg === null || attitude === null ? NO_DATA : attitude.rollDeg.toFixed(1);
  const pitchText = attitude?.pitchDeg === null || attitude === null ? NO_DATA : attitude.pitchDeg.toFixed(1);

  return (
    <div className="flex shrink-0 flex-col items-center gap-1">
      <svg
        viewBox="0 0 220 220"
        width={220}
        height={220}
        role="img"
        aria-label={
          drawable
            ? `姿态：横滚 ${roll.toFixed(1)} 度，俯仰 ${pitch.toFixed(1)} 度`
            : '姿态：没有姿态数据'
        }
        data-hgc-attitude={drawable ? 'live' : 'missing'}
      >
        <defs>
          <clipPath id={clipId}>
            <circle cx={110} cy={110} r={RADIUS} />
          </clipPath>
        </defs>

        {/* 底面：没有数据时它就是全部内容，明确写着"无姿态数据"而不是画成水平 */}
        <circle cx={110} cy={110} r={RADIUS} fill="var(--surface-3)" />
        {drawable ? (
          <>
            <g clipPath={`url(#${clipId})`}>
              <g transform={`rotate(${-roll} 110 110) translate(0 ${pitch * PX_PER_DEG})`}>
                <rect x={-120} y={-260} width={460} height={370} fill="var(--attitude-sky)" />
                <rect x={-120} y={110} width={460} height={370} fill="var(--attitude-ground)" />
                <line x1={-120} y1={110} x2={340} y2={110} stroke="var(--attitude-line)" strokeWidth={2} />
                <PitchLadder />
              </g>

              {/* 横滚刻度跟着地平线转，顶部固定的指针指向当前横滚角（真实姿态仪的读法） */}
              <g clipPath={`url(#${clipId})`} transform={`rotate(${-roll} 110 110)`}>
                <RollScale />
              </g>
            </g>

            <AircraftSymbol />
            <RollPointer />
          </>
        ) : (
          <>
            <text
              x={110}
              y={104}
              textAnchor="middle"
              fontSize={15}
              fontWeight={600}
              fill="var(--fg)"
            >
              无姿态数据
            </text>
            <text x={110} y={126} textAnchor="middle" fontSize={10} fill="var(--fg-muted)">
              {attitude === null ? '还没有收到任何帧' : '还没有收到 ATTITUDE'}
            </text>
          </>
        )}

        <circle cx={110} cy={110} r={RADIUS} fill="none" stroke="var(--line-strong)" strokeWidth={2} />
      </svg>

      <p className="m-0 flex items-baseline gap-3 text-xs">
        <span className="flex items-baseline gap-1">
          <span className="text-fg-subtle">横滚</span>
          <span className="font-mono tabular-nums text-fg">{rollText}°</span>
        </span>
        <span className="flex items-baseline gap-1">
          <span className="text-fg-subtle">俯仰</span>
          <span className="font-mono tabular-nums text-fg">{pitchText}°</span>
        </span>
      </p>
    </div>
  );
}

/** 俯仰梯：正角度实线、负角度虚线（上/下的区分不能只靠颜色） */
function PitchLadder(): JSX.Element {
  return (
    <g data-hgc-pitch-ladder="">
      {pitchMarks().map((mark) => {
        const y = 110 - mark.deg * PX_PER_DEG;
        const outer = mark.major ? 62 : 34;
        const inner = 18;
        const dash = mark.deg < 0 ? '6 5' : undefined;
        return (
          <g key={mark.deg}>
            {mark.deg === 0 ? null : (
              <>
                <line
                  x1={110 - outer}
                  y1={y}
                  x2={110 - inner}
                  y2={y}
                  stroke="var(--attitude-line)"
                  strokeWidth={mark.major ? 2 : 1}
                  strokeDasharray={dash}
                />
                <line
                  x1={110 + inner}
                  y1={y}
                  x2={110 + outer}
                  y2={y}
                  stroke="var(--attitude-line)"
                  strokeWidth={mark.major ? 2 : 1}
                  strokeDasharray={dash}
                />
              </>
            )}
            {mark.label === null ? null : (
              <text
                x={110}
                y={y + 3.5}
                textAnchor="middle"
                fontSize={11}
                fontWeight={600}
                fill="var(--attitude-mark)"
              >
                {mark.label}
              </text>
            )}
          </g>
        );
      })}
    </g>
  );
}

const ROLL_MARKS: readonly number[] = [0, 10, 20, 30, 45, 60];

/** 横滚刻度：0/±10/±20 短线，±30/±45/±60 长线且带数字 */
function RollScale(): JSX.Element {
  const items: JSX.Element[] = [];
  for (const angle of ROLL_MARKS) {
    for (const sign of angle === 0 ? [1] : [-1, 1]) {
      const deg = angle * sign;
      const major = angle >= 30;
      const outer = 88;
      const inner = major ? 76 : 81;
      const radians = deg * DEG;
      items.push(
        <line
          key={`tick-${deg}`}
          x1={110 + outer * Math.sin(radians)}
          y1={110 - outer * Math.cos(radians)}
          x2={110 + inner * Math.sin(radians)}
          y2={110 - inner * Math.cos(radians)}
          stroke="var(--attitude-mark)"
          strokeWidth={major ? 2 : 1}
        />,
      );
      if (major) {
        items.push(
          <text
            key={`label-${deg}`}
            x={110 + 64 * Math.sin(radians)}
            y={110 - 64 * Math.cos(radians) + 4}
            textAnchor="middle"
            fontSize={11}
            fontWeight={600}
            fill="var(--attitude-mark)"
          >
            {String(deg)}
          </text>,
        );
      }
    }
  }
  return <g data-hgc-roll-scale="">{items}</g>;
}

/** 顶部固定的横滚指针。刻度在转、指针不动 —— 这样"读出来的角"与"看到的斜"是同一个符号。 */
function RollPointer(): JSX.Element {
  return (
    <polygon
      data-hgc-roll-pointer=""
      points="110,22 103,6 117,6"
      fill="var(--attitude-mark)"
      stroke="var(--fg)"
      strokeWidth={0.5}
    />
  );
}

/** 固定的机体符号：与地平线无关，是"本机"的唯一参考 */
function AircraftSymbol(): JSX.Element {
  return (
    <g data-hgc-aircraft-symbol="">
      <line
        x1={70}
        y1={110}
        x2={98}
        y2={110}
        stroke="var(--attitude-mark)"
        strokeWidth={3}
        strokeLinecap="round"
      />
      <line
        x1={122}
        y1={110}
        x2={150}
        y2={110}
        stroke="var(--attitude-mark)"
        strokeWidth={3}
        strokeLinecap="round"
      />
      <circle cx={110} cy={110} r={3} fill="var(--attitude-mark)" stroke="var(--fg)" strokeWidth={0.5} />
    </g>
  );
}

const TAPE_WIDTH = 400;
const TAPE_HEIGHT = 62;
const TAPE_HALF_SPAN_DEG = 60;

function HeadingTape({ attitude }: { readonly attitude: AttitudeModel | null }): JSX.Element {
  const heading = attitude?.headingDeg ?? null;
  const ticks = heading === null ? [] : headingTicks(heading, TAPE_HALF_SPAN_DEG, 5);
  const cardinal = heading === null ? null : cardinalLabel(heading);
  const headingStale = attitude?.headingStale === true;
  const headingAgeLabel = attitude?.headingAgeLabel ?? '';

  return (
    <div className="flex min-w-0 flex-col gap-1">
      <div className="flex items-baseline gap-2">
        <span className="text-[10px] text-fg-subtle">航向</span>
        <span className="font-mono text-lg leading-tight tabular-nums text-fg">
          {heading === null ? NO_DATA : `${heading.toFixed(0)}°`}
        </span>
        {cardinal === null ? null : <span className="text-xs text-fg-muted">{cardinal}</span>}
        {headingStale ? (
          <span data-hgc-heading-stale="" className="text-[10px] font-semibold text-warning">
            航向数据已过期（{headingAgeLabel}更新）
          </span>
        ) : null}
      </div>

      <svg
        viewBox={`0 0 ${TAPE_WIDTH} ${TAPE_HEIGHT}`}
        width="100%"
        height={TAPE_HEIGHT}
        role="img"
        aria-label={heading === null ? '航向带：没有航向数据' : `航向带：当前航向 ${heading.toFixed(0)} 度`}
        data-hgc-heading-tape={heading === null ? 'missing' : 'live'}
        className="rounded-control border border-line bg-surface-2"
        preserveAspectRatio="none"
      >
        {heading === null ? (
          <text x={TAPE_WIDTH / 2} y={TAPE_HEIGHT / 2 + 4} textAnchor="middle" fontSize={12} fill="var(--fg-muted)">
            无航向数据
          </text>
        ) : (
          ticks.map((tick) => {
            const x = TAPE_WIDTH / 2 + tick.offset * (TAPE_WIDTH / 2);
            return (
              <g key={tick.deg}>
                <line
                  x1={x}
                  y1={tick.major ? 30 : 42}
                  x2={x}
                  y2={54}
                  stroke="var(--fg-muted)"
                  strokeWidth={tick.major ? 2 : 1}
                />
                {tick.label === null ? null : (
                  <text x={x} y={24} textAnchor="middle" fontSize={12} fontWeight={600} fill="var(--fg)">
                    {tick.label}
                  </text>
                )}
              </g>
            );
          })
        )}
        <polygon points={`${TAPE_WIDTH / 2},${TAPE_HEIGHT} ${TAPE_WIDTH / 2 - 6},${TAPE_HEIGHT - 8} ${TAPE_WIDTH / 2 + 6},${TAPE_HEIGHT - 8}`} fill="var(--accent)" />
        <line x1={TAPE_WIDTH / 2} y1={0} x2={TAPE_WIDTH / 2} y2={TAPE_HEIGHT} stroke="var(--accent)" strokeWidth={1} />
      </svg>

      <p className="m-0 text-[10px] text-fg-subtle">
        刻度每 5°，标注每 30°（北/东/南/西每 90°）；中央指针为本机航向。
      </p>
    </div>
  );
}

/** HUD 的容器：把运行时快照接进来，并按链路阶段解释"为什么没有数据"。 */
export function FlyHud(): JSX.Element {
  const live = useVehicleState();
  const status = useLinkStatus();
  const now = useNow(1000);

  /*
   * 断开之后 `LinkManager` **仍然保存着最后一帧状态**（复盘要用），但界面不能把它当实时值：
   * 一个不再更新的姿态看起来与实时的完全一样。所以这里按链路阶段决定要不要把它交给 HUD，
   * 而不是让 HUD 自己去猜 —— 判断只有一处，就不会有一个面板忘了判断。
   */
  const connected = status.phase === 'connected';
  const state = connected ? live : null;

  const emptyReason =
    connected && live === null
      ? '链路已连接，但还没有收到任何机体的帧 —— 第一帧到达时这里会自动出现姿态与读数。'
      : `链路未连接（${status.title}），没有实时姿态与读数。断开后不保留上一帧：那个数字只会越来越旧。`;

  return <Hud state={state} now={now} emptyReason={emptyReason} />;
}
